import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiDocLanding } from "@/content/naati-corpus/documents";
import { NAATI_DOC_META, NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

function docFaqs(d: NaatiDocLanding) {
  return [
    { q: `What is a ${d.titleEn.replace("NAATI Translation of ", "").toLowerCase()} translation used for?`, a: d.useCaseEn },
    { q: `What should I prepare before sending my ${d.officialEn.toLowerCase()}?`, a: d.prepareEn.join(" · ") },
    { q: `What are the most common mistakes with this document?`, a: d.pitfallsEn.join(" · ") },
    { q: `How long does it take and how is it priced?`, a: `${d.turnaroundEn} Pricing is per page or by word count, and every job starts with a free written quote.` },
    {
      q: "Can the translation be uploaded straight to ImmiAccount?",
      a: "Yes. The stamped PDF carrying the NAATI-certified translator's CPN uploads directly into ImmiAccount, and the same file is accepted by Immigration NZ, IRCC Canada and the UK Home Office.",
    },
    {
      q: "Do I need to send the original document?",
      a: "High-resolution colour scans are enough for the translation itself. Originals are needed only when MFA legalisation runs alongside it.",
    },
  ];
}

export const Route = createFileRoute("/en/naati/document/$doc")({
  loader: async ({ params }): Promise<{ d: NaatiDocLanding }> => {
    const { getNaatiDocLanding } = await import("@/content/naati-corpus/documents");
    const d = getNaatiDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: NaatiDocPageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Document type not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleEn} 2026 — CPN Certified Translator`;
    const description = `${d.titleEn} for Australian and New Zealand visa lodgement — ${d.turnaroundEn}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/naati/document/${params.doc}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/naati/document/${params.doc}` }],
        keywords: [
          d.titleEn.toLowerCase(),
          `naati translation ${d.slug.replace(/-/g, " ")}`,
          `certified thai to english ${d.slug.replace(/-/g, " ")} translation`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
              { name: d.titleEn, url: `${site}/en/naati/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(docFaqs(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/naati/document/${params.doc}`)) },
      ],
    };
  },
});

function NaatiDocPageEN() {
  const { d } = Route.useLoaderData();
  const faqs = docFaqs(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/naati" className="hover:underline">NAATI Translation</a> <span>/</span> <span>{d.titleEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleEn} — Australian &amp; NZ Visas (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground">
          Source document: <strong>{d.officialEn}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Free quote →</a>
          <a href="/en/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">NAATI FAQ</a>
          <a href={`/naati/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Where this document is used</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">What to prepare before translation</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Turnaround:</strong> {d.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Mistakes that get documents rejected</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — {d.titleEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other document types</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_DOC_META.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/en/naati/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn.replace("NAATI Translation of ", "")}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">NAATI translation by province</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_PROVINCE_META.map((p) => (
              <a key={p.slug} href={`/en/naati/province/${p.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                NAATI {p.provinceEn}
              </a>
            ))}
          </div>
        </section>

        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
